<template>
  <div class="chat-conv" :class="variant">
    <div ref="log" class="chat-log" @click="onBubbleClick">
      <div
        v-for="(m, i) in chat.messages"
        :key="i"
        class="msg"
        :class="[m.role, { reject: m.reject, building: m.building }]"
      >
        <div class="av" aria-hidden="true">
          <svg
            v-if="m.role === 'bot'"
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"/>
            <path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/>
          </svg>
          <span v-else>我</span>
        </div>
        <div class="msg-body">
          <div class="c" :class="{ think: m.thinking }" v-html="bubbleHtml(m)" />
          <feedback-bar
            v-if="m.showFeedback"
            :question="m.question"
            :ask-record-id="m.askRecordId"
            :conversation-id="m.conversationId"
          />
          <div v-if="m.suggest && m.suggest.length" class="preset">
            <span v-for="(s, si) in m.suggest" :key="si" @click="ask(s)">{{ s }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态推荐问题（豆包式「大家都在问」） -->
    <div v-if="showEmptyState" class="sug">
      <div class="sug-title">大家都在问</div>
      <div class="preset">
        <span v-for="p in chat.presets" :key="p.q" @click="ask(p.q)">{{ p.q }}</span>
      </div>
    </div>

    <div class="composer">
      <div class="composer-box" :class="{ disabled: !chat.online }">
        <textarea
          ref="ta"
          v-model="inputQ"
          rows="1"
          :disabled="!chat.online"
          :placeholder="chat.online ? '有问题，尽管问…' : '网络已断开，请稍后再试'"
          @input="autoGrow"
          @keydown.enter.exact.prevent="send"
        />
        <button
          v-show="inputQ.length"
          type="button"
          class="clear"
          aria-label="清空输入"
          title="清空输入"
          @click="clearInput"
        >
          <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="currentColor" />
            <path
              d="M8.4 8.4l7.2 7.2M15.6 8.4l-7.2 7.2"
              stroke="#fff"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="send"
          :disabled="!chat.online || chat.sending"
          aria-label="发送"
          @click="send"
        >
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 19V5"/>
            <path d="M5 12l7-7 7 7"/>
          </svg>
        </button>
      </div>
      <p class="composer-hint">内容由 AI 生成</p>
    </div>

    <video-player-dialog :show="videoShow" :src="videoSrc" @close="closeVideo" />
    <image-lightbox
      :show="imageShow"
      :list="imageList"
      :start-index="imageIndex"
      @close="closeImage"
    />
  </div>
</template>

<script>
import FeedbackBar from './FeedbackBar.vue'
import VideoPlayerDialog from './VideoPlayerDialog.vue'
import ImageLightbox from './ImageLightbox.vue'
import { renderMarkdown } from '@/utils/markdown'
import { getVideoSrc, markVideoLinks } from '@/utils/media'
import { chatState, ensureWelcome, loadPresets, sendQuestion } from '@/utils/chatStore'

const HTML_ESCAPE = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, c => HTML_ESCAPE[c])
}

export default {
  name: 'ChatConversation',
  components: { FeedbackBar, VideoPlayerDialog, ImageLightbox },
  props: {
    /** float：右下角悬浮窗内；inline：首页标签页内 */
    variant: { type: String, default: 'float' },
    showPresets: { type: Boolean, default: false }
  },
  data() {
    return {
      inputQ: '',
      videoShow: false,
      videoSrc: '',
      imageShow: false,
      imageList: [],
      imageIndex: 0
    }
  },
  computed: {
    chat() {
      return chatState
    },
    /** 只有欢迎语时展示推荐问题，提问后自动收起（豆包式空状态） */
    showEmptyState() {
      return this.showPresets && this.chat.presets.length > 0 && this.chat.messages.length <= 1
    }
  },
  mounted() {
    this.scroll()
    this.autoGrow()
  },
  created() {
    ensureWelcome()
    if (this.showPresets) loadPresets()
  },
  methods: {
    /** 气泡内容：AI 回答渲染 Markdown；用户输入原样转义展示 */
    bubbleHtml(m) {
      if (m.html) return m.html
      return m.role === 'me'
        ? escapeHtml(m.text)
        : markVideoLinks(renderMarkdown(m.text))
    },
    /** 气泡内点击：图片放大查看；视频链接弹窗播放，不跳走 */
    onBubbleClick(e) {
      const target = e.target
      if (!target || !target.closest) return

      const img = target.closest('img')
      if (img) {
        const src = img.currentSrc || img.getAttribute('src') || ''
        if (!src) return
        const bubble = img.closest('.c') || img.parentElement
        const list = this.collectImages(bubble)
        const i = list.indexOf(src)
        this.imageList = list.length ? list : [src]
        this.imageIndex = i >= 0 ? i : 0
        this.imageShow = true
        return
      }

      const anchor = target.closest('a')
      if (!anchor) return
      const src = getVideoSrc(anchor)
      if (!src) return
      e.preventDefault()
      this.videoSrc = src
      this.videoShow = true
    },
    /** 同一条气泡内的图片地址，供查看器左右切换 */
    collectImages(root) {
      if (!root || !root.querySelectorAll) return []
      return Array.from(root.querySelectorAll('img'))
        .map(i => i.currentSrc || i.getAttribute('src') || '')
        .filter(Boolean)
    },
    closeVideo() {
      this.videoShow = false
      this.videoSrc = ''
    },
    closeImage() {
      this.imageShow = false
      this.imageList = []
      this.imageIndex = 0
    },
    /**
     * 发送：先立即清空输入框（流式回答期间输入框不该还留着上一句），
     * 只有真正发送失败时才把内容回填，方便用户直接重发
     */
    async send() {
      const q = this.inputQ
      if (!q.trim() || this.chat.sending) return

      this.inputQ = ''
      this.$nextTick(() => this.autoGrow())

      const r = await sendQuestion(q, () => this.scroll())
      if (r.ok) return

      // 失败则回填，方便直接重发；若等待期间用户已重新输入则不覆盖
      if (!this.inputQ.trim()) {
        this.inputQ = q
        this.$nextTick(() => {
          this.autoGrow()
          this.focusInput()
        })
      }
      if (r.msg) this.$message.warning(r.msg)
    },
    focusInput() {
      const el = this.$refs.ta
      if (el && !el.disabled) el.focus()
    },
    /** 清空输入框：复位高度并保持焦点，方便直接重新输入 */
    clearInput() {
      if (!this.inputQ) return
      this.inputQ = ''
      this.$nextTick(() => {
        this.autoGrow()
        this.focusInput()
      })
    },
    async ask(q) {
      const r = await sendQuestion(q, () => this.scroll())
      if (r.msg) this.$message.warning(r.msg)
    },
    scroll() {
      this.$nextTick(() => {
        const el = this.$refs.log
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    /** 输入框随内容增高，最多 5 行 */
    autoGrow() {
      const el = this.$refs.ta
      if (!el) return
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 116)}px`
      this.scroll()
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-conv {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: $bg;
}

.chat-log {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 18px 16px 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.3) transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 116, 139, 0.28);
    border-radius: 3px;

    &:hover { background: rgba(100, 116, 139, 0.45); }
  }
}

.sug {
  padding: 2px 16px 12px;
  background: $bg;
  flex-shrink: 0;
}

.sug-title {
  font-size: 12px;
  color: $grey;
  margin-bottom: 9px;
  font-weight: 600;
  letter-spacing: 0.02em;
}



.composer {
  flex-shrink: 0;
  padding: 10px 16px 12px;
  border-top: 1px solid $line;
  background: $surface;
}

.composer-box {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: $bg;
  border: 1px solid $line;
  border-radius: 22px;
  padding: 5px 6px 5px 16px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus-within {
    border-color: $blue;
    background: $surface;
    box-shadow: 0 0 0 3px rgba(26, 115, 184, 0.1);
  }

  &.disabled { opacity: 0.6; }

  textarea {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: $txt;
    font-family: inherit;
    font-size: 13.5px;
    line-height: 1.6;
    padding: 7px 0;
    max-height: 116px;
    resize: none;
    overflow-y: auto;

    /* 多行输入时的右侧滚动条：细窄淡色，不打断圆角输入区的整体感 */
    scrollbar-width: thin;
    scrollbar-color: rgba(100, 116, 139, 0.25) transparent;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: rgba(100, 116, 139, 0.22);
      border-radius: 3px;

      &:hover { background: rgba(100, 116, 139, 0.42); }
    }

    &::placeholder { color: $grey; }
    &:disabled { cursor: not-allowed; }
  }
}

/* 输入内容的圆形 × 清空按钮，与发送按钮同样贴底对齐 */
.clear {
  width: 26px;
  height: 26px;
  margin-bottom: 3px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #C0C4CC;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;

  svg { display: block; }

  &:hover { color: #909399; transform: scale(1.06); }
  &:active { transform: scale(0.94); }
}

.send {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, $blue, $lblue);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.06);
    transform: scale(1.04);
  }

  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.composer-hint {
  text-align: center;
  color: $grey;
  font-size: 11px;
  margin-top: 8px;
  line-height: 1.4;
}

/* 首页标签页内：内嵌卡片中，加大左右留白
   对话区保持 $bg 浅底，AI 气泡为白底才不会与背景糊在一起 */
.chat-conv.inline {
  .chat-log {
    padding: 22px 24px 8px;
  }

  .sug {
    padding: 2px 24px 14px;
  }

  .composer {
    padding: 10px 24px 16px;
  }
}
</style>
